// @flow
import BaseModel from 'Stores/BaseModel';
import ListModel from 'Stores/ListModel';
import AssetModel from 'Stores/AssetModel';
import {
  formatDate,
  fromApiDate,
  addMonthsToDate,
  formatDateToISO,
  dateIsBefore,
  dateIsAfter,
  dateStartOfDay,
  dateEndOfDay,
} from 'Utils';
import { validateLength, validateImageSelect, isInvalid } from 'Utils/Validators';
import axios from 'axios';

declare var API_BASE_URL: string;

type Link = {
  id: number,
  title: string,
};

export default class NewsModel extends BaseModel {
  static types = [
    { value: 'news', label: 'News' },
    { value: 'event', label: 'Event' },
  ];

  id: number = 0;
  type: string = 'news';
  title: string = '';
  text: string = '';
  isTop: boolean = false;
  hidden: boolean = false;
  image: ?AssetModel = null;
  prev: ?Link = null;
  next: ?Link = null;
  gallery: Array<AssetModel> = [];
  datePublished: ?Date = new Date();
  dateUpdated: ?Date = new Date();
  startDate: ?Date = new Date();
  endDate: ?Date = new Date();
  managePublishing: string = 'online';
  publishingDateActivated: boolean = false;
  publishingDateStart: ?string = new Date();
  publishingDateEnd: ?string = null;
  consumptionInfo: ?string = null;
  consumptionInfoCustomDescriptionEnabled: ?boolean = false;
  consumptionInfoCustomDescription: ?string = null;

  constructor(data: Object = {}) {
    super();
    this.hydrate(data);

    this.image = data.image ? new AssetModel(data.image) : null;
    this.gallery = data.gallery ? data.gallery.map((image) => new AssetModel(image)) : [];
    this.datePublished = fromApiDate(data.datePublished);
    this.dateUpdated = fromApiDate(data.dateUpdated);
    this.startDate = fromApiDate(data.startDate);
    this.endDate = fromApiDate(data.endDate);
    this.publishingDateStart = fromApiDate(data.publishingDateStart);
    this.publishingDateEnd = data.publishingDateEnd ? fromApiDate(data.publishingDateEnd) : null;

    this.managePublishing =
      this.hidden && !this.isNew()
        ? 'offline'
        : this.publishingDateActivated
          ? 'timeset'
          : 'online';

    this.populatePublishingEndDate();
  }

  valid = () => {
    this.errors = {};
    validateLength(this.errors, 'title', this.title, 1, 80);
    validateImageSelect(this.errors, 'image', this.image);

    return !isInvalid(this.errors);
  };

  toListItem() {
    const subline = [this.isTop ? 'Top ' : ''];
    subline.push(this.isEvent() ? 'Event' : 'News');
    subline.push('|');
    subline.push(`Erstellt: ${formatDate(this.datePublished, 'dd.MM.yyyy')}`);

    if (this.publishingDateActivated && this.publishingDateStart && this.publishingDateEnd) {
      subline.push('|');
      subline.push(
        `Sichtbarkeit: ${formatDate(this.publishingDateStart, 'dd.MM.yyyy')} - ${formatDate(
          this.publishingDateEnd,
          'dd.MM.yyyy'
        )}`
      );
    } else if (this.publishingDateActivated && this.publishingDateStart) {
      subline.push('|');
      subline.push(`Sichtbarkeit: ${formatDate(this.publishingDateStart, 'dd.MM.yyyy')} - offen`);
    } else if (this.publishingDateActivated) {
      subline.push('|');
      subline.push('Sichtbarkeit: Manuell');
    }

    const now = new Date();
    const startOfPublishingStartDate = dateStartOfDay(this.publishingDateStart);
    const endOfPublishingEndDate = dateEndOfDay(this.publishingDateEnd);
    const isHidden =
      this.hidden ||
      (this.publishingDateActivated && dateIsBefore(now, startOfPublishingStartDate)) ||
      (this.publishingDateActivated && dateIsAfter(now, endOfPublishingEndDate));

    return {
      id: this.id,
      headline: this.title,
      subline: subline.join(' '),
      hidden: isHidden,
      image: this.image,
      model: this,
      publishingDateActivated: this.publishingDateActivated,
      publishingDateStart: this.publishingDateStart,
      publishingDateEnd: this.publishingDateEnd,
    };
  }

  isEvent(): boolean {
    return this.type === 'event';
  }

  save = () => {
    const data = this.toRequestJSON();

    if (this.id > 0) {
      return axios.patch(`${API_BASE_URL}/news/${this.id}/`, data).then((res) => {
        return new NewsModel(res.data);
      });
    }

    return axios.post(`${API_BASE_URL}/news/`, data).then((res) => {
      return new NewsModel(res.data);
    });
  };

  delete = () => {
    return axios.delete(`${API_BASE_URL}/news/${this.id}/`);
  };

  toJSON = () => {
    const json: Object = {
      type: this.type,
      title: this.title,
      text: this.text,
      isTop: this.isTop,
      hidden: this.hidden,
      image: this.image,
      gallery: this.gallery,
      publishingDateActivated: this.publishingDateActivated,
      publishingDateStart: this.publishingDateStart,
      publishingDateEnd: this.publishingDateEnd,
      consumptionInfo: this.consumptionInfo,
      consumptionInfoCustomDescriptionEnabled: !!this.consumptionInfoCustomDescriptionEnabled,
      consumptionInfoCustomDescription: this.consumptionInfoCustomDescription,
    };

    if (!this.isNew()) {
      json.id = this.id;
    }

    if (this.isEvent()) {
      json.startDate = this.startDate;
      json.endDate = this.endDate;
    }

    return json;
  };

  /**
   * Process request data to ensure proper casting and formatting.
   *
   * @return {Object}
   */
  toRequestJSON: Object = () => {
    const res: Object = { ...this.toJSON() };
    // cast date field to ISO 8601 string securely due to formatting errors with
    // timezone when axios uses JSON.stringify() to cast date objects to string
    const dateFields: Array = ['endDate', 'startDate', 'publishingDateStart', 'publishingDateEnd'];
    dateFields.forEach((field) => {
      if (res.hasOwnProperty(field)) {
        res[field] = !!res[field] ? formatDateToISO(res[field]) : null;
      }
    });

    return res;
  };

  populatePublishingEndDate = (): ?Date => {
    // populate only for new event records
    if (this.isEvent() && this.isNew() && !this.publishingDateEnd) {
      this.publishingDateEnd = addMonthsToDate(this.startDate, 3);

      return this.publishingDateEnd;
    }

    return null;
  };

  static getAll = (params: Object = {}) => {
    return axios({
      method: 'get',
      url: `${API_BASE_URL}/news/`,
      params,
    }).then((res: { data: Array<Object> }) => {
      return new ListModel(res.data, NewsModel);
    });
  };
}
